import type { PriceBookColumnKey, PriceBookContent } from '@/blocks/domain/types';
import {
  PRICE_BOOK_SECTIONS,
  priceBookSectionColumns,
  priceBookSectionMeta,
  type PriceBookSection,
  type PriceBookSectionKey
} from '@/blocks/domain/helpers/priceBookSections';

export interface PriceBookSectionColumnView {
  key: PriceBookColumnKey;
  label: string;
  token: string;
}

export interface PriceBookSectionView {
  key: PriceBookSectionKey;
  label: string;
  rowHeader: string;
  nameToken: string;
  meta: Array<{ key: PriceBookColumnKey; token: string }>;
  columns: PriceBookSectionColumnView[];
}

const FALLBACK_LABELS: Readonly<Record<PriceBookColumnKey, string>> = {
  name: 'Name',
  description: 'Description',
  job_type: 'Job type',
  flat_price: 'Flat price',
  unit: 'Unit',
  rate: 'Rate',
  ot_rate: 'OT Rate',
  ot_rate_2: 'OT Rate 2',
  volume_rate: 'Volume Rate',
  mobilization: 'Mobilization',
  travel: 'Travel',
  min_charge: 'Min charge'
};

export function priceBookToken(key: string): string {
  return `{{priceBook.${key}}}`;
}

function nameTokenFor(section: PriceBookSection): string {
  if (section.key === 'equipment') return priceBookToken('name');
  return priceBookToken(section.rowHeader.toLowerCase());
}

function visibleKeys(content: PriceBookContent): string[] {
  const columns = Array.isArray(content.columns) ? content.columns : [];
  if (columns.length === 0) return PRICE_BOOK_SECTIONS.flatMap((section) => [...section.columns]);
  return columns.filter((column) => column.visible).map((column) => column.key);
}

function labelFor(content: PriceBookContent, key: PriceBookColumnKey): string {
  const authored = content.columns?.find((column) => column.key === key)?.label?.trim();
  return authored || FALLBACK_LABELS[key] || key;
}

/**
 * The authoring shape shown before any price book is bound (`previewValueBinding === 'unbound'`):
 * three tables with their own first column and column subset, each with one row of locked tokens.
 * No money values exist here, so no currency formatting runs.
 */
export function buildPriceBookSectionsView(content: PriceBookContent): PriceBookSectionView[] {
  const keys = visibleKeys(content);
  return PRICE_BOOK_SECTIONS.map((section) => ({
    key: section.key,
    label: section.label,
    rowHeader: section.rowHeader,
    nameToken: nameTokenFor(section),
    meta: priceBookSectionMeta(section).map((key) => ({ key, token: priceBookToken(key) })),
    columns: priceBookSectionColumns(section, keys).map((key) => ({ key, label: labelFor(content, key), token: priceBookToken(key) }))
  }));
}
